import React, { useState } from 'react';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Button,
} from '@mui/material';

interface Dependente {
  nomeDependente: string;
  grauParentescoNome: string;
  codigoGrauParentesco: number;
  codigoSituacao: number;
}

interface Contato {
  tipo: string;
  descricaoContato: string;
}

interface Associado {
  nome: string;
  cpf: string;
  dataDeNascimento: string;
  dependentes: Dependente[];
  contatos: Contato[];
  codigo: number;
  nomeFantasiaDaEmpresa: string;
  codigoDaEmpresa?: number;
}

interface CsvData {
  nome: string;
  email: string;
  link: string;
}

const GDFAlert: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [stats, setStats] = useState({ processed: 0, success: 0, errors: 0 });
  const [csvData, setCsvData] = useState<CsvData[]>([]);

  // Padroniza CPF para 11 dígitos
  const padronizarCPF = (cpf: string): string => {
    return cpf.replace(/\D/g, '').padStart(11, '0');
  };

  // Função para exportar CSV
  const exportToCSV = () => {
    if (csvData.length === 0) return;

    const headers = ['Nome', 'Email', 'Link'];
    const csvRows = [
      headers.join(','),
      ...csvData.map(
        item =>
          `"${item.nome.replace(/"/g, '""')}","${item.email}","${item.link}"`,
      ),
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'dados_associados.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Processa cada linha do TXT
  const processarLinha = async (cpfInput: string) => {
    const cpf = padronizarCPF(cpfInput);

    if (!/^\d{11}$/.test(cpf)) {
      setLogs(prev => [
        ...prev,
        `ERRO: ${cpfInput} → CPF inválido após formatação`,
      ]);
      return false;
    }

    try {
      // 1. Consulta API
      const response = await axios.get(
        `${process.env.REACT_APP_URL_BASE}/v2/api/Associados?token=${process.env.REACT_APP_TOKEN_S4E_V2}&cpfAssociado=${cpf}`,
      );

      // 2. Validações (GDFC, situação ativa)
      const associado = response.data.dados?.find(
        (a: Associado) =>
          a.codigoDaEmpresa === 27855 &&
          a.dependentes?.some(
            d =>
              d.codigoGrauParentesco === 1 &&
              [1, 16, 111, 116, 118, 119].includes(d.codigoSituacao),
          ),
      );

      if (!associado) {
        setLogs(prev => [
          ...prev,
          `AVISO: ${cpf} → Não é titular ativo da GDFC`,
        ]);
        return false;
      }

      // 3. Gera e envia link
      const { linkGerado } = (
        await axios.get(
          `${process.env.REACT_APP_OG_API_URL}/generate/AssinaturaGdf?id_associado=${associado.codigo}`,
        )
      ).data;

      const celular = associado.contatos?.find(
        c => c.tipo === 'Celular',
      )?.descricaoContato;

      const email =
        associado.contatos?.find(c => c.tipo === 'E-mail')?.descricaoContato ||
        'Sem email cadastrado';

      // Adiciona dados ao CSV
      setCsvData(prev => [
        ...prev,
        {
          nome: associado.nome,
          email: email,
          link: linkGerado,
        },
      ]);

      if (celular) {
        await axios.post(
          `${process.env.REACT_APP_OG_API_URL}/register/SendWhatsApp`,
          {
            number: celular.replace(/\D/g, ''),
            name: associado.nome,
            id: linkGerado.replace('https://assina.ae/', ''),
            vendedor: 65083,
          },
        );
      }

      setLogs(prev => [
        ...prev,
        `SUCESSO: ${cpf} → Link enviado para ${celular || 'sem celular'}`,
      ]);
      return true;
    } catch (error) {
      setLogs(prev => [
        ...prev,
        `ERRO: ${cpf} → ${
          error instanceof Error ? error.message : 'Falha na API'
        }`,
      ]);
      return false;
    }
  };

  // Processa o arquivo completo
  const processarArquivo = async () => {
    if (!file) return;

    setStats({ processed: 0, success: 0, errors: 0 });
    setLogs(['Iniciando processamento...']);
    setCsvData([]); // Limpa dados anteriores

    const reader = new FileReader();
    reader.onload = async e => {
      const text = e.target?.result as string;
      const cpfs = text.split('\n').filter(line => line.trim());

      for (const cpf of cpfs) {
        const success = await processarLinha(cpf.trim());
        setStats(prev => ({
          processed: prev.processed + 1,
          success: success ? prev.success + 1 : prev.success,
          errors: success ? prev.errors : prev.errors + 1,
        }));
        await new Promise(resolve => setTimeout(resolve, 10000)); // Delay de 10s
      }

      setLogs(prev => [...prev, 'Processamento concluído!']);

      // Exporta CSV automaticamente ao final
      if (csvData.length > 0) {
        exportToCSV();
      }
    };
    reader.readAsText(file);
  };

  // Interface
  return (
    <Container>
      <Paper elevation={3} sx={{ p: 4, my: 4 }}>
        <Typography variant="h5" gutterBottom>
          Processador de CPFs
        </Typography>

        <input
          type="file"
          accept=".txt"
          onChange={e => setFile(e.target.files?.[0] || null)}
        />

        <Button
          variant="contained"
          onClick={processarArquivo}
          disabled={!file}
          sx={{ mt: 2, mr: 2 }}
        >
          Processar
        </Button>

        <Button
          variant="outlined"
          onClick={exportToCSV}
          disabled={csvData.length === 0}
          sx={{ mt: 2 }}
        >
          Exportar CSV
        </Button>

        <Box sx={{ mt: 4 }}>
          <Typography>
            Status: {stats.processed} processados | {stats.success} sucessos |{' '}
            {stats.errors} erros
          </Typography>
          <Box
            sx={{
              maxHeight: 300,
              overflow: 'auto',
              mt: 2,
              p: 2,
              bgcolor: '#f5f5f5',
            }}
          >
            {logs.map((log, i) => (
              <Typography key={i} fontFamily="monospace">
                {log}
              </Typography>
            ))}
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default GDFAlert;
